import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { BASE_URL, ensureCsrf, post } from 'src/common/api';

// Same sign-in as every Auto Appy product: the backend runs the OAuth flow
// with api.autoappy.com and keeps an HttpOnly session.
export const AuthContext = createContext(null);
const PROFILE_KEY = 'autoappy.gmb.auth_profile';

const readCached = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(PROFILE_KEY));
    return cached?.expiresAt > Date.now() ? cached.data : null;
  } catch { return null; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readCached);
  const [loading, setLoading] = useState(!readCached());
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      await ensureCsrf();
      const response = await api.get('/api/auth/me');
      const profile = response.data.data;
      setUser(profile);
      setError(null);
      try { localStorage.setItem(PROFILE_KEY, JSON.stringify({ data: profile, expiresAt: Date.now() + (profile.profile_cache_ttl_seconds || 300) * 1000 })); } catch { /* private mode */ }
      return profile;
    } catch (failure) {
      try { localStorage.removeItem(PROFILE_KEY); } catch { /* private mode */ }
      setUser(null);
      setError(failure.response?.status === 403 ? 'access_denied' : (failure.response ? null : 'offline'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const signIn = useCallback((returnTo = `${window.location.pathname}${window.location.search}`) => {
    const params = new URLSearchParams({ return_to: returnTo || '/' });
    const workspace = new URLSearchParams(window.location.search).get('workspace');
    if (workspace) params.set('workspace', workspace);
    window.location.assign(`${BASE_URL}/auth/autoappy/redirect?${params}`);
  }, []);

  const signInSample = useCallback((as = 'owner') => {
    window.location.assign(`${BASE_URL}/auth/dev/login${as === 'member' ? '?as=member' : ''}`);
  }, []);

  const signOut = useCallback(async () => {
    try { await post('/api/auth/logout'); } finally {
      try { localStorage.removeItem(PROFILE_KEY); } catch { /* private mode */ }
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, refresh, signIn, signInSample, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
